import type { CanEntitlement } from '#layers/access/public';
import { computed } from 'vue';
import { useEntitlement } from '#layers/access/public';

interface AssignableItem {
	id: string;
}

/**
 * Entitlement allowlist gating for a relation-assignment field (role/policy). Resolves the ids the
 * user may assign for the given `assign` entitlement — `'*'` means all (wildcard/admin), otherwise
 * the specific ids — and derives the connect and per-id disconnect gates from it.
 *
 * Connect is an any-id gate: the user may add an item as long as they may assign at least one.
 * Disconnect is gated per id so assigning item A can never let the user remove item B; the same
 * allowlist is re-applied at save time via `allowedDisconnections` so a staged removal can never
 * detach an item the user may not assign.
 */
export function useAssignmentEntitlement(entitlement: CanEntitlement) {
	const { allowedIds } = useEntitlement();

	const allowedAssignableIds = computed(() => allowedIds(entitlement));
	const canAssign = computed(() => allowedAssignableIds.value === '*' || allowedAssignableIds.value.length > 0);

	function isAllowedId(id: string) {
		return allowedAssignableIds.value === '*' || allowedAssignableIds.value.includes(id);
	}

	function canDisconnectItem(item: AssignableItem) {
		return isAllowedId(item.id);
	}

	function allowedDisconnections(ids: string[]) {
		return ids.filter((id) => isAllowedId(id));
	}

	return {
		allowedAssignableIds,
		canAssign,
		canDisconnectItem,
		allowedDisconnections,
	};
}
